import { readItemField } from '../compile/item-field-reader';
import { phpJsonString } from '../compile/php-json-values';
import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from '../compile/php-decimal-values';

/** Full creator document contract, shared by cold compilation and persisted writes.
 * Preview serialization deliberately does not enter this guard. Extensions retain
 * their values and precision; known sections only determine canonical field order. */
export type CreatureAppearance = Record<string, unknown>;
export type AppearanceHelmetState = 'closed' | 'open' | 'off';
const MAX_BYTES = 262144, MAX_STRING = 512, MAX_DEPTH = 12;
const sections = ['schema', 'seed', 'rigAbi', 'headSocketAbi', 'body', 'head', 'eyebrows', 'eyelashes', 'makeup',
    'complexion', 'bodyArt', 'hair', 'facialHair', 'surface', 'preview'];
const bytes = (value: string): number => new TextEncoder().encode(value).length;
const record = (value: unknown): value is CreatureAppearance => value !== null && typeof value === 'object' && !Array.isArray(value);

function refuse(reason: string): never { throw new Error(`appearance-document-refused:${reason}`); }

/** Old writers changed hideHelmet alone. Preserve that last-writer interpretation. */
export function appearanceHelmetState(appearance: unknown): AppearanceHelmetState {
    const visibility = record(appearance) && record(appearance.equipmentVisibility) ? appearance.equipmentVisibility : {};
    const state = visibility.helmetState, legacy = visibility.hideHelmet;
    if (state === 'closed' || state === 'open' || state === 'off') {
        return typeof legacy === 'boolean' && legacy !== (state === 'off') ? (legacy ? 'off' : 'closed') : state;
    }
    return legacy === true ? 'off' : 'closed';
}

function checkShape(node: unknown, depth = 0): void {
    if (depth > MAX_DEPTH) refuse('nesting-too-deep');
    if (node === null || typeof node === 'boolean') return;
    if (typeof node === 'number' && Number.isFinite(node)) return;
    if (typeof node === 'string') { if (bytes(node) > MAX_STRING) refuse('string-too-long'); return; }
    if (typeof node !== 'object') refuse('unsupported-value');
    for (const [key, value] of Object.entries(node)) {
        if (bytes(key) > MAX_STRING) refuse('key-too-long');
        checkShape(value, depth + 1);
    }
}

/** Null/absent remains compatible for creatures with no creator document. */
export function creatureAppearanceDocument(raw: unknown): CreatureAppearance | null {
    if (raw == null || raw === false || raw === '') return null;
    if (typeof raw === 'string') {
        raw = raw.trim();
        if (raw === '' || raw === 'null') return null;
        if (bytes(raw as string) > MAX_BYTES) refuse('document-too-large');
        try { raw = JSON.parse(raw as string); } catch { refuse('invalid-json'); }
    }
    if (!record(raw)) refuse('object-required');
    if (typeof raw.schema !== 'string' || !raw.schema.trim()) refuse('schema-required');
    checkShape(raw);
    let equipment = raw.equipmentVisibility;
    if (record(equipment)) {
        const visibility = { ...equipment };
        if (visibility.helmetState === 'closed' || visibility.helmetState === 'open' || visibility.helmetState === 'off' || typeof visibility.hideHelmet === 'boolean') {
            const state = appearanceHelmetState(raw);
            visibility.helmetState = state;
            visibility.hideHelmet = state === 'off';
        } else delete visibility.helmetState;
        equipment = visibility;
    }
    const equipmentOnly = raw.schema === 'ft.creature-equipment-appearance.v1' && record(equipment)
        && (typeof equipment.hideHelmet === 'boolean' || equipment.hoodState === 'up' || equipment.hoodState === 'down');
    if (raw.body == null && raw.head == null && !equipmentOnly) refuse('body-or-head-required');
    for (const section of ['body', 'head']) {
        if (raw[section] != null && typeof raw[section] !== 'object') refuse(`${section}-container-required`);
    }
    const appearanceDoc: CreatureAppearance = {};
    for (const key of new Set([...sections, ...Object.keys(raw)])) {
        if (Object.prototype.hasOwnProperty.call(raw, key)) Object.defineProperty(appearanceDoc, key, {
            value: key === 'equipmentVisibility' ? equipment : raw[key], enumerable: true, writable: true, configurable: true,
        });
    }
    if (bytes(JSON.stringify(appearanceDoc)) > MAX_BYTES) refuse('document-too-large');
    return appearanceDoc;
}

/** Only an intentional persistence operation requires a complete document. */
export function encodeCreatureAppearance(raw: unknown): string {
    const appearanceDoc = creatureAppearanceDocument(raw);
    if (!appearanceDoc) refuse('document-not-ready');
    return JSON.stringify(appearanceDoc);
}

const bodyControls = [['ft.body.height', .9, 1.1], ['ft.body.size', -1, 1], ['ft.body.composition', -1, 1],
    ['ft.body.breast', -.75, 1], ['ft.body.abdomen', -.5, 1], ['ft.body.hips', -.75, .75]] as const;
// The existing shared decimal algorithm also handles the body's persistence precision of three places.
const roundBodyDecimal = roundPhpDecimal as (value: number, places: number) => number;

/** The narrow stored-body contract, shared by cold reads and creator projection. */
export function creatureBodyAppearanceDocument(raw: unknown): CreatureAppearance | null {
    try {
        if (raw == null || raw === '') return null;
        if (typeof raw === 'string') raw = JSON.parse(raw);
        if (!record(raw)) return null;
        const value = readItemField;
        const schema = value(raw, 'schema');
        const appearanceSchema = phpJsonString(value(raw, 'appearanceSchema')).trim(), controlContract = phpJsonString(value(raw, 'controlContract')).trim();
        const presentation = phpJsonString(value(raw, 'presentation')).trim(), presetRaw = value(raw, 'presetId');
        const preset = presetRaw == null || presetRaw === '' ? null : phpJsonString(presetRaw).trim();
        if (phpJsonString(schema).trim() !== 'ft.encounter-creature.body-appearance.v1' || appearanceSchema !== 'ft.appearance.humanoid.v2-candidate'
            || controlContract !== 'ft.humanoid.body-controls.v1' || !['feminine', 'masculine'].includes(presentation)
            || preset !== null && !['lean', 'average', 'athletic', 'powerful', 'soft_curvy', 'heavy', 'heavy_strong'].includes(preset)) return null;
        const rawValues = value(raw, 'values'); if (!record(rawValues)) return null;
        const values: CreatureAppearance = {};
        for (const [id, min, max] of bodyControls) {
            const field = value(rawValues, id);
            if (!isPhpNumeric(field) || phpJsonFloat(field) < min || phpJsonFloat(field) > max || !Number.isFinite(phpJsonFloat(field))) return null;
            const rounded = roundBodyDecimal(phpJsonFloat(field), 3); values[id] = Math.abs(rounded) < .0005 ? 0 : rounded;
        }
        return { schema: 'ft.encounter-creature.body-appearance.v1', appearanceSchema, controlContract, presentation, presetId: preset, values };
    } catch { return null; }
}

/** Unprojectable/head-only/equipment documents preserve the previously saved body. */
export function deriveCreatureBodyAppearance(appearance: unknown): CreatureAppearance | null {
    if (typeof appearance === 'string') {
        try { appearance = JSON.parse(appearance); } catch { return null; }
    }
    if (!record(appearance)) return null;
    const body = appearance.body;
    if (!record(body) || !record(body.values)) return null;
    const values: CreatureAppearance = {};
    for (const [id] of bodyControls) {
        if (!Object.prototype.hasOwnProperty.call(body.values, id) || !isPhpNumeric(body.values[id])) return null;
        values[id] = phpJsonFloat(body.values[id]);
    }
    const candidate = {
        schema: 'ft.encounter-creature.body-appearance.v1', appearanceSchema: 'ft.appearance.humanoid.v2-candidate',
        controlContract: 'ft.humanoid.body-controls.v1', presentation: body.presentation ?? null, presetId: body.presetId ?? null, values,
    };
    return creatureBodyAppearanceDocument(candidate) ?? creatureBodyAppearanceDocument({ ...candidate, presetId: null });
}

/** One accepted document supplies both fixed save fields; an explicit body overrides projection. */
export function attachCreatureAppearanceSave(data: FormData): void {
    const raw = data.get('appearance');
    if (!raw) return;
    const appearance = creatureAppearanceDocument(raw);
    if (!appearance) refuse('document-not-ready');
    data.set('appearance', JSON.stringify(appearance));
    if (!data.has('body_appearance')) {
        const body = deriveCreatureBodyAppearance(appearance);
        if (body) data.set('body_appearance', JSON.stringify(body));
    }
}
